import React from 'react';
import { Box, Typography } from '@mui/material';

// Token mappings:
// --fg4 → grey.400
// --fg2 → text.secondary
// --fg1 → text.primary
// --divider → divider

export interface FeatureStudioDetailHeaderProps {
  kind: 'Product' | 'Feature' | 'User Story';
  title: string;
  subtitle: string;
}

const LAPTOP_FIT_QUERY = '@media (min-width: 1280px) and (min-height: 768px)';

/**
 * FeatureStudioDetailHeader - Selection-aware header showing eyebrow, title, and description
 *
 * Variants:
 * - Product: "PRODUCT" eyebrow, product name, product description
 * - Feature: "FEATURE" eyebrow, feature name, feature description
 * - Story: "USER STORY" eyebrow, full ID + name, As-a-want-so-that format
 */
export const FeatureStudioDetailHeader: React.FC<FeatureStudioDetailHeaderProps> = ({
  kind,
  title,
  subtitle,
}) => {
  return (
    <Box
      data-testid="feature-studio-detail-header"
      sx={{
        padding: '16px 24px 12px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        flexShrink: 0,
        [LAPTOP_FIT_QUERY]: {
          minHeight: 46,
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: 'auto minmax(160px, 0.7fr) minmax(240px, 1.3fr)',
          alignItems: 'center',
          columnGap: 1.5,
          padding: '7px 16px',
        },
      }}
    >
      {/* Eyebrow */}
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'grey.400',
          marginBottom: '6px',
          [LAPTOP_FIT_QUERY]: { marginBottom: 0 },
        }}
      >
        {kind}
      </Typography>

      {/* Title */}
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: 'text.primary',
          marginBottom: '6px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          [LAPTOP_FIT_QUERY]: { marginBottom: 0, fontSize: 15, lineHeight: 1.25 },
        }}
      >
        {title}
      </Typography>

      {/* Subtitle */}
      <Typography
        title={subtitle}
        sx={{
          fontSize: 12.5,
          fontWeight: 400,
          color: 'text.secondary',
          lineHeight: 1.5,
          [LAPTOP_FIT_QUERY]: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
          },
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
};
